import {
  IX10ConfigChange,
  IX10Configuration,
  IX10ConfigurationUpdate,
  X10ConfigBlockedReason,
  X10ConfigItemStatus,
  X10ConfigKey
} from "../../../../../store/x10config/types";

export const VESSEL_OFFSET_LIMIT = 3;
export const AMBIENT_OFFSET_LIMIT = 5;
export const VOLUME_MIN_LITERS = 1;
export const VOLUME_MAX_LITERS = 10;
export const STEP = 0.1;

/** Unsent edits; a field is undefined until the user touches it. */
export interface IX10ConfigDraft {
  vesselOffset?: number;
  ambientOffset?: number;
  halo?: boolean;
  coolingFailMode?: boolean;
  volumeLeftLiters?: number;
}

/** Rounds to one decimal, the firmware's (and jcore's) step. */
export const roundToStep = (value: number) => Math.round(value * 10) / 10;

export const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

/** What the controls show when the user has not touched them. */
export const currentValues = (configuration: IX10Configuration) => ({
  // Offsets never set from the cloud are the firmware default, 0 °C.
  vesselOffset: configuration.vesselOffset.desired ?? 0,
  ambientOffset: configuration.ambientOffset.desired ?? 0,
  halo: configuration.halo.desired ?? true,
  // Fail mode belongs to the unit: show what it last reported.
  coolingFailMode: configuration.coolingFailMode.reported ?? false,
  volumeLeftLiters: clamp(
    roundToStep(configuration.volume.kegLiters ?? VOLUME_MAX_LITERS),
    VOLUME_MIN_LITERS,
    VOLUME_MAX_LITERS
  )
});

/** The settings to send: only the touched fields that differ from what the unit has (one message for all). */
export const buildUpdate = (
  draft: IX10ConfigDraft,
  configuration: IX10Configuration
): IX10ConfigurationUpdate => {
  const current = currentValues(configuration);
  const update: IX10ConfigurationUpdate = {};
  if (
    draft.vesselOffset !== undefined &&
    roundToStep(draft.vesselOffset) !== current.vesselOffset
  ) {
    update.vesselOffset = roundToStep(draft.vesselOffset);
  }
  if (
    draft.ambientOffset !== undefined &&
    roundToStep(draft.ambientOffset) !== current.ambientOffset
  ) {
    update.ambientOffset = roundToStep(draft.ambientOffset);
  }
  if (draft.halo !== undefined && draft.halo !== current.halo) {
    update.halo = draft.halo;
  }
  if (draft.coolingFailMode !== undefined && draft.coolingFailMode !== current.coolingFailMode) {
    update.coolingFailMode = draft.coolingFailMode;
  }
  // A volume overwrite is an action, not a setting: send it whenever it was touched.
  if (draft.volumeLeftLiters !== undefined) {
    update.volumeLeftLiters = roundToStep(draft.volumeLeftLiters);
  }
  return update;
};

export const formatOffset = (value: number | null | undefined) => {
  if (value === null || value === undefined) {
    return "--";
  }
  const rounded = roundToStep(value);
  return `${rounded > 0 ? "+" : ""}${rounded.toFixed(1)} °C`;
};

export const formatLiters = (value: number | null | undefined) =>
  value === null || value === undefined ? "--" : `${Number(value).toFixed(1)} L`;

export type Tone = "success" | "warning" | "alert" | "neutral";

export interface IStatusText {
  key: string;
  fallback: string;
  tone: Tone;
}

/** How a key's sync status reads in Control Tower. */
export const statusText = (key: X10ConfigKey, status: X10ConfigItemStatus): IStatusText => {
  switch (status) {
    case "PENDING":
      return { key: "status_pending", fallback: "Sending…", tone: "neutral" };
    case "SENT":
      return { key: "status_sent", fallback: "Sent, waiting for the unit", tone: "neutral" };
    case "UNCONFIRMED":
      return key === "V_OFFSET" || key === "A_OFFSET" || key === "HALO"
        ? {
            key: "status_unconfirmed_resend",
            fallback: "Not confirmed. It will be sent again when the unit is back",
            tone: "warning"
          }
        : {
            key: "status_unconfirmed",
            fallback: "Not confirmed. The unit may have been offline",
            tone: "warning"
          };
    case "DELIVERED":
      if (key === "V_FAIL_MODE") {
        return {
          key: "status_delivered_fail_mode",
          fallback: "Delivered, waiting for the unit's report (up to 15 min)",
          tone: "neutral"
        };
      }
      if (key === "VOLUME_LEFT") {
        return {
          key: "status_delivered_volume",
          fallback: "Delivered, waiting for the unit's volume",
          tone: "neutral"
        };
      }
      return { key: "status_delivered", fallback: "Delivered", tone: "success" };
    case "VERIFIED":
      return { key: "status_verified", fallback: "Confirmed by the unit", tone: "success" };
    case "MISMATCH":
      return key === "V_FAIL_MODE"
        ? {
            key: "status_mismatch_fail_mode",
            fallback: "The unit kept fail mode on. The vessel probe still looks faulty",
            tone: "warning"
          }
        : {
            key: "status_mismatch",
            fallback: "The unit reports a different value",
            tone: "warning"
          };
    case "OVERRIDDEN_BY_DEVICE":
      return {
        key: "status_overridden",
        fallback: "Turned back on by the unit. The vessel probe looks faulty",
        tone: "warning"
      };
    case "NOT_DELIVERED":
      return {
        key: "status_not_delivered",
        fallback: "Not delivered. Send it again",
        tone: "alert"
      };
    case "SUPERSEDED":
      return { key: "status_superseded", fallback: "Replaced by a newer change", tone: "neutral" };
    case "FAILED":
      return { key: "status_failed", fallback: "Not sent", tone: "alert" };
    default:
      return { key: "status_unknown", fallback: status, tone: "neutral" };
  }
};

export const blockedText = (
  reason: X10ConfigBlockedReason,
  retryAfterSeconds: number | null
): { key: string; fallback: string } => {
  switch (reason) {
    case "FEATURE_DISABLED":
      return {
        key: "blocked_feature_disabled",
        fallback: "Remote configuration is not enabled in this environment."
      };
    case "READ_ONLY_ROLE":
      return {
        key: "blocked_read_only",
        fallback: "Your role can view this configuration but not change it."
      };
    case "NOT_PROVISIONED":
      return { key: "blocked_not_provisioned", fallback: "This unit is not provisioned." };
    case "OFFLINE_OR_UNVERIFIED":
      return {
        key: "blocked_offline",
        fallback:
          "Changes are only sent to a unit heard from in the last 2 minutes, because it does not report its settings back."
      };
    case "BUSY":
      return {
        key: "blocked_busy",
        fallback: `A change was just sent. You can send another in ${retryAfterSeconds ?? 1} s.`
      };
    case "FIRMWARE_UNSUPPORTED":
      return {
        key: "blocked_firmware",
        fallback: "The unit's firmware does not support this setting (needs 1.0.69 or newer)."
      };
    case "NO_ACTIVE_KEG":
      return { key: "blocked_no_keg", fallback: "No keg is mounted on the unit." };
    default:
      return { key: "blocked_unknown", fallback: reason };
  }
};

export const changeSummary = (change: IX10ConfigChange | null) =>
  change ? { by: change.requestedBy, at: change.sentAt || change.requestedAt } : null;
